import api from "../../../utils/api";

// ============================================================================
// CRM Bookings — frontend service. Talks to the torre-de-control-api
// crmBookings module (Agenda / Calendario de reservas).
//
//   Bookings:
//     • list     GET    /crm/bookingsList       ?from&to&status
//     • single   GET    /crm/bookings?id=
//     • create   POST   /crm/bookings
//     • update   PUT    /crm/bookings?id=
//     • delete   DELETE /crm/bookings?id=
//   Time blocks:
//     • list     GET    /crm/bookings/blocks    ?from&to
//     • create   POST   /crm/bookings/blocks
//     • update   PUT    /crm/bookings/blocks?id=
//     • delete   DELETE /crm/bookings/blocks?id=
//   Availability:
//     • get      GET    /crm/bookings/availability
//     • save     PUT    /crm/bookings/availability
//     • gstatus  GET    /crm/bookings/google/status
//
// Times are ISO-8601 strings that carry the configured UTC offset, so
// `new Date(iso)` yields the correct absolute instant. Render wall-clock with
// the helpers below (which respect the business timezone).
// ============================================================================

export type BookingStatus =
  | "requested"
  | "confirmed"
  | "cancelled"
  | "completed"
  | "no_show";

export type BookingSource = "landing" | "manual";

export interface Booking {
  id: string;
  title: string | null;
  type: string | null;
  name: string | null;
  email: string | null;
  /** Number that IS a WhatsApp line (the landing asks explicitly). */
  whatsapp: string | null;
  /** Number that is NOT a WhatsApp line — dial it, don't open wa.me/. */
  phone?: string | null;
  company: string | null;
  notes: string | null;
  start: string; // ISO with offset
  end: string;
  timezone: string | null;
  status: BookingStatus;
  source: BookingSource;
  leadId: string | null;
  /** Human agent who owns this meeting (writes to THEIR Google Calendar). */
  agentId: string | null;
  googleEventId: string | null;
  meetingUrl: string | null;
}

export interface TimeBlock {
  id: string;
  title: string | null;
  reason: string | null;
  start: string;
  end: string;
  allDay: boolean;
  /** Human agent who owns this block (mirrored to THEIR Google Calendar). */
  agentId?: string | null;
  googleEventId?: string | null;
}

export interface AvailabilityWindow {
  start: string; // "09:00"
  end: string; // "17:00"
}

export interface Availability {
  id?: string;
  timezone: string;
  utcOffset: string; // "-04:00"
  slotMinutes: number;
  bufferMinutes: number;
  minLeadHours: number;
  maxAdvanceDays: number;
  weekly: Record<string, AvailabilityWindow[]>; // "0".."6"
  googleCalendar: {
    connected: boolean;
    calendarId: string;
    createMeet: boolean;
  };
  /** Responsable por producto: reparte las citas y los leads/negocios nuevos. */
  assignment?: OwnerAssignment;
}

/** Quién atiende cada producto. Sin agente del producto se usa el predeterminado. */
export interface OwnerAssignment {
  byProduct: Record<string, string>;
  defaultAgentId: string | null;
}

/** Cambios al reparto: `null` quita al agente de ese producto. */
export interface OwnerAssignmentPatch {
  byProduct?: Record<string, string | null>;
  defaultAgentId?: string | null;
}

export interface GoogleStatus {
  configured: boolean;
  connected: boolean;
  calendarId: string;
  createMeet: boolean;
  reason: string | null;
}

// --- helpers to unwrap the API envelope ------------------------------------
// Single-resource handlers use response(res, ..., { message, data }) →
// axios body is { meta, data: { message, data } } → the resource is data.data.
function single<T>(res: { data?: { data?: { data?: T } } }): T | null {
  return res.data?.data?.data ?? null;
}

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------
export interface BookingsListParams {
  from?: string;
  to?: string;
  status?: BookingStatus;
  agentId?: string;
  limit?: number;
}

export async function fetchBookings(params: BookingsListParams = {}): Promise<Booking[]> {
  const res = await api.get("/crm/bookingsList", { params: { limit: 500, ...params } });
  const body = res.data ?? {};
  return Array.isArray(body.data) ? body.data : [];
}

export interface BookingInput {
  title?: string;
  type?: string;
  name?: string;
  email?: string;
  /** Number that IS a WhatsApp line. */
  whatsapp?: string;
  /** Number that is NOT a WhatsApp line — dial it, don't open wa.me/. */
  phone?: string;
  company?: string;
  notes?: string;
  start: string;
  end: string;
  status?: BookingStatus;
  timezone?: string;
  /** Assign the meeting to a human agent → writes to their Google Calendar. */
  agentId?: string;
  /** Link the meeting to a CRM lead. */
  leadId?: string;
}

export async function createBooking(input: BookingInput): Promise<Booking | null> {
  const res = await api.post("/crm/bookings", input);
  return single<Booking>(res);
}

export async function updateBooking(id: string, patch: Partial<BookingInput>): Promise<Booking | null> {
  const res = await api.put("/crm/bookings", patch, { params: { id } });
  return single<Booking>(res);
}

export async function deleteBooking(id: string): Promise<void> {
  await api.delete("/crm/bookings", { params: { id } });
}

// ---------------------------------------------------------------------------
// Demos — schedule a demo from a landing lead. Creates a `demo` booking linked
// to the lead + agent, writes the event to the agent's Google Calendar with the
// prospect as an attendee (dual calendar + Meet link), and emails a themed
// invitation (Meet link + .ics fallback) to both. See POST /crm/bookings/demo.
// ---------------------------------------------------------------------------
export interface DemoInput {
  leadId: string;
  agentId: string;
  start: string; // ISO with offset
  end?: string;
  durationMinutes?: number;
  title?: string;
  notes?: string;
  /** Override / confirm the prospect's email (defaults to the lead's email). */
  email?: string;
  timezone?: string;
}

/** The created demo booking plus whether the invitation email went out. */
export type DemoBooking = Booking & { emailSent?: boolean };

export async function scheduleDemo(input: DemoInput): Promise<DemoBooking | null> {
  const res = await api.post("/crm/bookings/demo", input);
  return single<DemoBooking>(res);
}

// ---------------------------------------------------------------------------
// Time blocks
// ---------------------------------------------------------------------------
export async function fetchBlocks(params: { from?: string; to?: string; agentId?: string } = {}): Promise<TimeBlock[]> {
  const res = await api.get("/crm/bookings/blocks", { params });
  return (single<TimeBlock[]>(res) as TimeBlock[] | null) ?? [];
}

export interface BlockInput {
  title?: string;
  reason?: string;
  start: string;
  end: string;
  allDay?: boolean;
  /** Assign the block to a human agent → mirrored on their Google Calendar. */
  agentId?: string;
}

export async function createBlock(input: BlockInput): Promise<TimeBlock | null> {
  const res = await api.post("/crm/bookings/blocks", input);
  return single<TimeBlock>(res);
}

export async function updateBlock(id: string, patch: Partial<BlockInput>): Promise<TimeBlock | null> {
  const res = await api.put("/crm/bookings/blocks", patch, { params: { id } });
  return single<TimeBlock>(res);
}

export async function deleteBlock(id: string): Promise<void> {
  await api.delete("/crm/bookings/blocks", { params: { id } });
}

// ---------------------------------------------------------------------------
// Availability + Google Calendar
// ---------------------------------------------------------------------------
export async function getAvailability(): Promise<Availability | null> {
  const res = await api.get("/crm/bookings/availability");
  return single<Availability>(res);
}

export async function saveAvailability(patch: Partial<Availability>): Promise<Availability | null> {
  const res = await api.put("/crm/bookings/availability", patch);
  return single<Availability>(res);
}

/** Guarda el responsable por producto (pide el permiso `manage_global`). */
export async function saveOwnerAssignment(assignment: OwnerAssignmentPatch): Promise<OwnerAssignment | null> {
  const res = await api.put("/crm/bookings/availability", { assignment });
  return single<Availability>(res)?.assignment ?? null;
}

export async function getGoogleStatus(): Promise<GoogleStatus | null> {
  const res = await api.get("/crm/bookings/google/status");
  return single<GoogleStatus>(res);
}

// ===========================================================================
// Date helpers — render wall-clock in the business timezone (IANA tz).
// ===========================================================================
export const WEEKDAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
export const WEEKDAYS_LONG_ES = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];
export const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const pad = (n: number) => String(n).padStart(2, "0");

export interface TzParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  weekday: number; // 0=Sun..6=Sat
  dateKey: string; // yyyy-mm-dd
}

/** Break a Date into wall-clock parts in the given IANA timezone. */
export function tzParts(date: Date, timeZone: string): TzParts {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    weekday: "short",
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  let hour = Number(parts.hour);
  if (hour === 24) hour = 0; // some engines emit 24 for midnight
  const minute = Number(parts.minute);
  const wkMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year,
    month,
    day,
    hour,
    minute,
    weekday: wkMap[parts.weekday as string] ?? 0,
    dateKey: `${year}-${pad(month)}-${pad(day)}`,
  };
}

export function fmtTime(iso: string, timeZone: string): string {
  const { hour, minute } = tzParts(new Date(iso), timeZone);
  return `${pad(hour)}:${pad(minute)}`;
}

export function fmtDateLong(iso: string, timeZone: string): string {
  const p = tzParts(new Date(iso), timeZone);
  return `${WEEKDAYS_LONG_ES[p.weekday]} ${p.day} de ${MONTHS_ES[p.month - 1]}, ${p.year}`;
}

/** Build "yyyy-mm-dd" for a Date in a timezone. */
export function dateKey(date: Date, timeZone: string): string {
  return tzParts(date, timeZone).dateKey;
}

export const BOOKING_STATUS_META: Record<BookingStatus, { label: string; tone: string; dot: string }> = {
  requested: { label: "Solicitada", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400", dot: "bg-amber-500" },
  confirmed: { label: "Confirmada", tone: "bg-emerald-500/15 text-emerald-500", dot: "bg-emerald-500" },
  cancelled: { label: "Cancelada", tone: "bg-gray-400/15 text-gray-500", dot: "bg-gray-400" },
  completed: { label: "Completada", tone: "bg-sky-500/15 text-sky-500", dot: "bg-sky-500" },
  no_show: { label: "No asistió", tone: "bg-red-500/15 text-red-500", dot: "bg-red-500" },
};
